import React from "react";
import moment from "moment";
import CompanyInformation from "../utils/companyDetails";

function createStreetViewLink(lat, lng) {
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
}

const Step3ReviewSubmit = ({
  watch,
  selectedLocation,
  posts,
  customerCompanyLocal,
  serviceList,
  errors,
  err,
  errType,
}) => {
  const formData = watch();

  const getServiceName = (serviceTypeValue) => {
    const serviceNames = [
      "Standing Post",
      "Mobile Patrol", 
      "Workplace Security",
      "Event Security",
      "Executive Protection",
      "Emergency",
    ];
    const index = parseInt(serviceTypeValue, 10) - 1;
    return index >= 0 && index < serviceNames.length
      ? serviceNames[index]
      : "Unknown Service";
  };

  const formatShiftTime = (shift) => {
    if (!shift.date || !shift.startTime || !shift.endTime) return "Not set";
    
    const date = moment(shift.date).format("MMM DD, YYYY");
    const startTime = moment(shift.startTime).format("h:mm A");
    const endTime = moment(shift.endTime).format("h:mm A");
    
    return `${date} from ${startTime} to ${endTime}`;
  };

  const calculateShiftDuration = (shift) => {
    if (!shift.startTime || !shift.endTime) return "N/A";
    
    const duration = moment(shift.endTime).diff(moment(shift.startTime), "hours", true);
    return `${duration.toFixed(1)} hours`;
  };

  return (
    <div className="space-y-4">
      {/* <div className="text-center">
        <h3 className="text-lg font-semibold mb-1 text-brand-blue-dark">
          Review Your Job Details
        </h3>
        <p className="text-sm text-muted-foreground">
          Please review all information before submitting your job request
        </p>
      </div> */}

      {/* Service Details */}
      <div className="rounded-md p-4 bg-white/95 backdrop-blur-sm border border-border shadow-sm">
        <h4 className="text-sm font-semibold mb-3 flex items-center text-brand-blue-dark">
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Service Details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Service Type</label>
            <p className="text-sm mt-0.5 font-medium text-brand-blue-dark">
              {getServiceName(formData.serviceType)}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Max. Hourly Rate</label>
            <p className="text-sm mt-0.5 font-medium text-brand-blue-dark">
              ${formData.hourlyPayRate || "Not set"}
            </p>
          </div>
        </div>
        {formData.memo && (
          <div className="mt-3">
            <label className="text-xs font-medium text-muted-foreground">Memo</label>
            <p className="text-sm mt-0.5 bg-background p-2.5 rounded border border-border text-brand-blue-dark">
              {formData.memo}
            </p>
          </div>
        )}
      </div>

      {/* Location & Posts */}
      <div className="rounded-md p-4 bg-white/95 backdrop-blur-sm border border-border shadow-sm">
        <h4 className="text-sm font-semibold mb-3 flex items-center text-brand-blue-dark">
          <svg
            className="w-4 h-4 mr-2 text-brand-pink"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          Location & Posts
        </h4>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Job Location</label>
          <p className="text-sm mt-0.5 font-medium text-brand-blue-dark">
            {selectedLocation?.formatted_address || "Not selected"}
          </p>
        </div>
        {posts.length > 0 && (
          <div className="mt-3">
            <label className="text-xs font-medium text-muted-foreground">Posts ({posts.length})</label>
            <div className="mt-1.5 space-y-1.5">
              {posts.map((post, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-background p-2.5 rounded border border-border"
                >
                  <span className="text-sm text-brand-blue-dark">{post.note}</span>
                  <a
                    href={createStreetViewLink(post.position.lat, post.position.lng)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs hover:underline text-brand-pink"
                  >
                    View Location
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Shifts */}
      <div className="rounded-md p-4 bg-white/95 backdrop-blur-sm border border-border shadow-sm">
        <h4 className="text-sm font-semibold mb-3 flex items-center text-brand-blue-dark">
          <svg
            className="w-4 h-4 mr-2 text-brand-pink"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Shifts ({formData.shifts?.length || 0})
        </h4>
        <div className="space-y-2">
          {formData.shifts?.map((shift, index) => (
            <div
              key={index}
              className="bg-background p-3 rounded border border-border"
            >
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-medium text-brand-blue-dark">
                  Shift {index + 1}
                </h5>
                <span className="text-xs font-medium px-2 py-1 rounded bg-brand-pink/10 text-brand-pink">
                  {shift.staffsRequired || 0} staff{(shift.staffsRequired || 0) !== 1 ? 's' : ''} required
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Schedule</label>
                  <p className="text-brand-blue-dark">{formatShiftTime(shift)}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Duration</label>
                  <p className="text-brand-blue-dark">{calculateShiftDuration(shift)}</p>
                </div>
              </div>
            </div>
          )) || (
            <p className="text-sm text-muted-foreground">No shifts defined</p>
          )}
        </div>
      </div>

      {/* Customer Company Information */}
      {Object.keys(customerCompanyLocal).length > 0 && (
        <div className="rounded-md p-4 bg-white/95 backdrop-blur-sm border border-border shadow-sm">
          <h4 className="text-sm font-semibold mb-3 flex items-center text-brand-blue-dark">
            <svg
              className="w-4 h-4 mr-2 text-brand-pink"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H7m2 0v-4a2 2 0 012-2h2a2 2 0 012 2v4m-6 0V9a2 2 0 012-2h2a2 2 0 012 2v7"
              />
            </svg>
            Customer Information
          </h4>
          <div className="text-brand-blue-dark">
            <CompanyInformation customerCompany={customerCompanyLocal} />
          </div>
        </div>
      )}

      {/* Error Message */}
      {err && errType === "advance" && (
        <div className="rounded-md p-3 bg-destructive/10 border border-destructive/30">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-4 w-4 text-destructive"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-destructive">
                There was an error submitting your job
              </h3>
              <div className="mt-1 text-sm text-destructive">
                <span dangerouslySetInnerHTML={{ __html: err }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="rounded-md p-3 bg-brand-blue-light/10 border border-brand-blue-light/30">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-4 w-4 text-brand-blue-dark"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-brand-blue-dark">
              Ready to submit?
            </h3>
            <div className="mt-1 text-sm text-brand-blue-dark/80">
              <p>
                By clicking "Post Job" below, you'll create a new work order that will be
                available for security officers to view and apply for.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3ReviewSubmit;
